/* eslint-disable max-lines */
import { EyeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSearch } from "../../hooks/useSearch";
import {
  FeedbackStatus,
  ICustomerFeedback
} from "../../interfaces/endpoints/ICustomerFeedback";
import { getAllFeedback } from "../../services/CustomerFeedback.service";
import { formatRouteName } from "../../utils/customerFeedback/labels";
import AuditAuthor from "../audit-author/AuditAuthor";
import Table, { Row } from "../ui/table";
import Pagination from "../ui/table/Pagination";
import SuperAdmin from "../users/permissions/SuperAdmin";
import FeedbackFilters from "./FeedbackFilters";
import SourceBadge from "./SourceBadge";
import StatusBadge from "./StatusBadge";
import UpdateStatusModal from "./UpdateStatusModal";

const PAGE_SIZE = 10;

const columns: (string | number)[] = [
  "Dërguar",
  "Burimi",
  "Statusi",
  "Linja",
  "Data e kërkimit",
  "Email",
  "Audit",
  "Veprimet"
];

const useFeedbackQueryParams = () => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const source = searchParams.get("source");
    const status = searchParams.get("status");
    const hasEmail = searchParams.get("hasEmail");
    const route = searchParams.get("route");
    const dateFrom = searchParams.get("dateFrom");
    const dateTo = searchParams.get("dateTo");

    const out: { [k: string]: string } = { pageSize: String(PAGE_SIZE) };
    if (source) out.source = source;
    if (status) out.status = status;
    if (hasEmail) out.hasEmail = hasEmail;
    if (route) out.route = route;
    if (dateFrom) out.dateFrom = dateFrom;
    if (dateTo) out.dateTo = dateTo;

    return out;
  }, [searchParams]);
};

const CustomerFeedbackTable: FC = () => {
  const otherQuery = useFeedbackQueryParams();
  const { response, loading, handlePageChange, page, setResponse } = useSearch(
    getAllFeedback,
    {
      withPagination: true,
      paginationSelector: "pageIndex",
      otherQuery
    }
  );

  const [statusEditTarget, setStatusEditTarget] = useState<{
    id: number;
    status: FeedbackStatus;
  } | null>(null);

  const handleStatusUpdated = (id: number, newStatus: FeedbackStatus) => {
    setResponse((prev) => {
      if (!prev) return prev;
      const data = prev.data as unknown as {
        result?: ICustomerFeedback[];
        [key: string]: unknown;
      };
      if (!data?.result) return prev;
      const next = {
        ...prev,
        data: {
          ...data,
          result: data.result.map((row) =>
            row.id === id ? { ...row, status: newStatus } : row
          )
        }
      };
      return next as unknown as typeof prev;
    });
  };

  // Cast through unknown — see comment above.
  const responseData = response?.data as unknown as
    | { result?: ICustomerFeedback[]; totalCount?: number }
    | undefined;
  const items = responseData?.result ?? [];
  const totalCount = responseData?.totalCount ?? 0;

  return (
    <>
      <FeedbackFilters />

      <div className="flex flex-col" style={{ height: "calc(100vh - 14rem)" }}>
        <Table
          titles={columns}
          loading={loading}
          className="md:table"
          style="hover"
        >
          {items.length ? (
            items.map((row: ICustomerFeedback) => (
              <Row
                key={row.id}
                submitted={
                  <span className="text-sm text-tableColor">
                    {row.auditAuthor?.insertDate
                      ? dayjs(row.auditAuthor.insertDate).format(
                          "DD MMM YYYY · HH:mm"
                        )
                      : "-"}
                  </span>
                }
                source={<SourceBadge source={row.source} />}
                status={<StatusBadge status={row.status} />}
                route={
                  <span className="text-sm text-tableColor">
                    <span className="font-medium">
                      {formatRouteName(row.routeOrigin)}
                    </span>
                    <span className="mx-1.5 text-gray-400">→</span>
                    <span className="font-medium">
                      {formatRouteName(row.routeDestination)}
                    </span>
                  </span>
                }
                searchDate={
                  <span className="text-sm text-tableColor">
                    {row.searchDate || "-"}
                  </span>
                }
                email={
                  row.email ? (
                    <span className="text-sm text-tableColor">{row.email}</span>
                  ) : (
                    <span className="text-sm italic text-gray-400">anonim</span>
                  )
                }
                audit={
                  <AuditAuthor
                    createdByName={row.auditAuthor?.createdByName}
                    createdByLastName={row.auditAuthor?.createdByLastName}
                    updatedByName={row.auditAuthor?.updatedByName}
                    updatedByLastName={row.auditAuthor?.updatedByLastName}
                    insertDate={row.auditAuthor?.insertDate}
                    updateDate={row.auditAuthor?.updateDate}
                  />
                }
                actions={
                  <span className="flex items-center gap-1">
                    <Link
                      to={`/customer-feedback/detail/${row.id}`}
                      aria-label="Shiko detajet"
                      className="p-2.5 md:p-1"
                    >
                      <EyeIcon className="w-5 h-5 md:w-4 md:h-4" />
                    </Link>
                    <SuperAdmin>
                      <button
                        type="button"
                        aria-label="Ndrysho statusin"
                        className="p-2.5 md:p-1"
                        onClick={() =>
                          setStatusEditTarget({
                            id: row.id,
                            status: row.status
                          })
                        }
                      >
                        <PencilSquareIcon className="w-5 h-5 md:w-4 md:h-4" />
                      </button>
                    </SuperAdmin>
                  </span>
                }
              />
            ))
          ) : (
            <></>
          )}
        </Table>

        <Pagination
          onPageChange={handlePageChange}
          totalCount={totalCount}
          length={items.length}
          page={page}
          pageSize={PAGE_SIZE}
        />
      </div>

      {statusEditTarget && (
        <UpdateStatusModal
          show
          feedbackId={statusEditTarget.id}
          currentStatus={statusEditTarget.status}
          onClose={() => setStatusEditTarget(null)}
          onUpdated={(newStatus) => {
            handleStatusUpdated(statusEditTarget.id, newStatus);
          }}
        />
      )}
    </>
  );
};

export default CustomerFeedbackTable;
